import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import {
  fetchLeaveTypes, fetchHolidays, fetchHolidayCalendars,
  fetchWorkflowTemplates, addHolidayApi, createLeaveTypeApi,
  fetchPayrollTemplates, createPayrollTemplateApi,
} from '../services/api';
import type { PayrollTemplateComponent } from '../services/api';
import Chip from '../components/Chip';
import Switch from '../components/Switch';
import Alert from '../components/Alert';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import DatePicker from '../components/DatePicker';

const SYSTEM_EVENTS = [
  { code: 'joining',      label: 'Joining' },
  { code: 'confirmation', label: 'Confirmation' },
  { code: 'transfer',     label: 'Transfer' },
  { code: 'separation',   label: 'Separation' },
] as const;

interface LocalRule {
  id: string;
  eventCode: string;
  templateId: string;
  isAuto: boolean;
}

export default function Settings() {
  const { role, toast } = useAppStore();
  const queryClient = useQueryClient();

  const { data: leaveTypes = [], isLoading: ltLoading } = useQuery({ queryKey: qk.leaveTypes(), queryFn: fetchLeaveTypes });
  const { data: holidays = [] } = useQuery({ queryKey: qk.holidays(), queryFn: fetchHolidays });
  const { data: holidayCalendars = [] } = useQuery({ queryKey: qk.holidayCalendars(), queryFn: fetchHolidayCalendars, enabled: role === 'hr' });
  const { data: workflowTemplates = [] } = useQuery({ queryKey: qk.workflowTemplates(), queryFn: fetchWorkflowTemplates });
  const { data: payrollTemplates = [] } = useQuery({ queryKey: qk.payrollTemplates(), queryFn: fetchPayrollTemplates, enabled: role === 'hr' });

  // ── Automation events ──────────────────────────────────────────────────────
  const [customEvents, setCustomEvents] = useState<{ code: string; label: string }[]>([]);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventDesc, setNewEventDesc] = useState('');

  // ── Automation rules ───────────────────────────────────────────────────────
  const initialRules = useMemo<LocalRule[]>(() =>
    workflowTemplates.map(t => ({ id: t.id, eventCode: t.trigger, templateId: t.id, isAuto: true })),
    [workflowTemplates],
  );
  const [rules, setRules] = useState<LocalRule[] | null>(null);
  const effectiveRules = rules ?? initialRules;
  const [showAddRule, setShowAddRule] = useState(false);
  const [newRule, setNewRule] = useState<{ eventCode: string; templateId: string; isAuto: boolean }>({
    eventCode: 'joining', templateId: '', isAuto: true,
  });

  const allEvents = [...SYSTEM_EVENTS, ...customEvents];

  // ── Leave type add ─────────────────────────────────────────────────────────
  const [showAddLt, setShowAddLt] = useState(false);
  const [ltForm, setLtForm] = useState({ name: '', code: '' });

  const addLtMutation = useMutation({
    mutationFn: createLeaveTypeApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.leaveTypes() });
      toast('Leave type added');
      setShowAddLt(false);
      setLtForm({ name: '', code: '' });
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  // ── Holiday add ────────────────────────────────────────────────────────────
  const activeCalendar = holidayCalendars.find(c => c.isActive) ?? holidayCalendars[0];
  const [showAddHol, setShowAddHol] = useState(false);
  const [holForm, setHolForm] = useState({ name: '', holidayDate: '', type: 'national' as 'national' | 'regional' | 'optional' });

  const addHolMutation = useMutation({
    mutationFn: addHolidayApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.holidays() });
      toast('Holiday added');
      setShowAddHol(false);
      setHolForm({ name: '', holidayDate: '', type: 'national' });
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  // ── Payroll template add ───────────────────────────────────────────────────
  const [showAddPt, setShowAddPt] = useState(false);
  const [expandedPtId, setExpandedPtId] = useState<string | null>(null);
  const [ptForm, setPtForm] = useState<{ name: string; currency: string; components: PayrollTemplateComponent[] }>({
    name: '', currency: 'INR',
    components: [{ code: 'BASIC', kind: 'earning', formula: 'BASIC' }],
  });

  const addPtMutation = useMutation({
    mutationFn: () => createPayrollTemplateApi({ name: ptForm.name.trim(), currency: ptForm.currency, components: ptForm.components }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.payrollTemplates() });
      toast('Payroll template created');
      setShowAddPt(false);
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const addPtComponent = () =>
    setPtForm(f => ({ ...f, components: [...f.components, { code: '', kind: 'earning' as const, formula: '' }] }));
  const removePtComponent = (i: number) =>
    setPtForm(f => ({ ...f, components: f.components.filter((_, j) => j !== i) }));
  const updatePtComponent = (i: number, patch: Partial<PayrollTemplateComponent>) =>
    setPtForm(f => ({ ...f, components: f.components.map((c, j) => j === i ? { ...c, ...patch } : c) }));

  // ── Display helpers ────────────────────────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const thisYear = new Date().getFullYear();
  const sortedHolidays = [...holidays].sort((a, b) => a.date.localeCompare(b.date));
  const fmtDate = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  if (role !== 'hr') {
    return (
      <div>
        <div className="sk-page-header"><h1>Settings</h1></div>
        <Alert variant="info">Settings are only accessible to HR administrators.</Alert>
      </div>
    );
  }

  return (
    <div>
      <div className="sk-page-header"><h1>Settings</h1></div>

      {/* ── Automation events ── */}
      <div className="t-card" style={{ marginBottom: 20 }}>
        <div className="card-head">
          <div>
            <h3>Automation events</h3>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>Lifecycle events that can trigger a workflow</div>
          </div>
          <Button variant="outline" onClick={() => { setNewEventName(''); setNewEventDesc(''); setShowAddEvent(true); }}>
            <Icon name="plus" size={13} color="currentColor" /> Add event
          </Button>
        </div>
        <div className="card-body" style={{ paddingTop: 12 }}>
          <Alert variant="info" style={{ marginBottom: 16 }}>
            Built-in events come from the lifecycle engine and can't be removed. Add your own (e.g. <em>Role change</em>, <em>Return from leave</em>) to automate beyond them — the <strong>steps</strong> for each event live in its workflow template, authored in Workflows.
          </Alert>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {SYSTEM_EVENTS.map(ev => (
              <div key={ev.code} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 999, fontSize: 'var(--fs-sm)', background: 'var(--surface)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ink-3)', flexShrink: 0 }} />
                <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{ev.label}</span>
                <span style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-xs)' }}>system</span>
              </div>
            ))}
            {customEvents.map(ev => (
              <div key={ev.code} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 999, fontSize: 'var(--fs-sm)', background: 'var(--surface)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{ev.label}</span>
                <span style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-xs)' }}>custom</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Automation rules ── */}
      <div className="t-card" style={{ marginBottom: 20 }}>
        <div className="card-head">
          <div>
            <h3>Automation rules</h3>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>Event → workflow to run, and whether it runs automatically</div>
          </div>
          <Button variant="accent" onClick={() => { setNewRule({ eventCode: allEvents[0]?.code ?? 'joining', templateId: workflowTemplates[0]?.id ?? '', isAuto: true }); setShowAddRule(true); }}>
            <Icon name="plus" size={13} color="white" /> Add rule
          </Button>
        </div>
        <div>
          {effectiveRules.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 0, padding: '8px 20px 6px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--ink-3)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>WHEN THIS EVENT...</span>
              <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--ink-3)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>...RUN THIS WORKFLOW</span>
              <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--ink-3)', letterSpacing: '0.05em', textTransform: 'uppercase', minWidth: 160 }}>MODE</span>
            </div>
          )}
          {effectiveRules.length === 0 ? (
            <div style={{ padding: '16px 20px', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>No rules yet. Add a rule to automate workflow launches.</div>
          ) : effectiveRules.map((rule, i) => (
            <div key={rule.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'center', padding: '10px 20px', borderBottom: i < effectiveRules.length - 1 ? '1px solid var(--border-soft, var(--border))' : 'none' }}>
              <select className="t-input" value={rule.eventCode} onChange={e => setRules(prev => (prev ?? initialRules).map(r => r.id === rule.id ? { ...r, eventCode: e.target.value } : r))}>
                {allEvents.map(ev => <option key={ev.code} value={ev.code}>{ev.label}</option>)}
              </select>
              <select className="t-input" value={rule.templateId} onChange={e => setRules(prev => (prev ?? initialRules).map(r => r.id === rule.id ? { ...r, templateId: e.target.value } : r))}>
                {workflowTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 160 }}>
                <Switch checked={rule.isAuto} onChange={checked => setRules(prev => (prev ?? initialRules).map(r => r.id === rule.id ? { ...r, isAuto: checked } : r))} />
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', minWidth: 60 }}>{rule.isAuto ? 'Auto-run' : 'Manual'}</span>
                <button onClick={() => setRules(prev => (prev ?? initialRules).filter(r => r.id !== rule.id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 4, lineHeight: 0, borderRadius: 4 }}>
                  <Icon name="trash" size={14} color="currentColor" />
                </button>
              </div>
            </div>
          ))}
          <div style={{ padding: '12px 20px', borderTop: effectiveRules.length > 0 ? '1px solid var(--border)' : 'none' }}>
            <Alert variant="info">
              <strong>Auto-run</strong> starts the workflow automatically when the event fires (adding an employee fires Joining). <strong>Manual</strong> means HR starts it from Workflows → Start a run. Blocking steps can never be skipped either way.
            </Alert>
          </div>
        </div>
      </div>

      {/* ── Leave types + Holiday calendar — two-col ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

        {/* Leave types */}
        <div className="t-card">
          <div className="card-head" style={{ paddingBottom: 12 }}>
            <div>
              <h3>Leave types &amp; accrual</h3>
            </div>
            <Button variant="outline" onClick={() => { setLtForm({ name: '', code: '' }); setShowAddLt(true); }}>
              <Icon name="plus" size={13} color="currentColor" /> Add type
            </Button>
          </div>
          <table className="t-table">
            <thead>
              <tr><th>TYPE</th><th>CODE</th><th>ACCRUAL POLICY</th></tr>
            </thead>
            <tbody>
              {ltLoading ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</td></tr>
              ) : leaveTypes.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>No leave types configured.</td></tr>
              ) : leaveTypes.map(lt => (
                <tr key={lt.id}>
                  <td style={{ fontWeight: 500 }}>{lt.name}</td>
                  <td><Chip variant="neutral">{lt.code}</Chip></td>
                  <td style={{ color: 'var(--ink-2)', fontSize: 'var(--fs-sm)' }}>{lt.annualDays} days / year</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Holiday calendar */}
        <div className="t-card">
          <div className="card-head" style={{ paddingBottom: 12 }}>
            <div>
              <h3>Holiday calendar</h3>
              {activeCalendar && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>{thisYear} · {activeCalendar.name}</div>}
            </div>
            <Button variant="outline" onClick={() => { setHolForm({ name: '', holidayDate: '', type: 'national' }); setShowAddHol(true); }} disabled={!activeCalendar}>
              <Icon name="plus" size={13} color="currentColor" /> Add holiday
            </Button>
          </div>
          <div>
            {sortedHolidays.length === 0 ? (
              <div style={{ padding: '16px 20px', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>No holidays configured.</div>
            ) : sortedHolidays.map((h, i) => {
              const isPast = h.date < today;
              return (
                <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: i < sortedHolidays.length - 1 ? '1px solid var(--border-soft, var(--border))' : 'none', opacity: isPast ? 0.45 : 1 }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 'var(--fs-sm)', color: 'var(--ink)' }}>{h.name}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 1 }}>
                      <Chip variant={h.type === 'national' ? 'info' : h.type === 'regional' ? 'accent' : 'neutral'}>{h.type}</Chip>
                    </div>
                  </div>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums', flexShrink: 0, marginLeft: 12 }}>{fmtDate(h.date)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Payroll templates ── */}
      <div className="t-card" style={{ marginBottom: 20 }}>
        <div className="card-head">
          <div>
            <h3>Payroll templates</h3>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>Define salary components and formulas for each payroll configuration</div>
          </div>
          <Button variant="outline" onClick={() => {
            setPtForm({ name: '', currency: 'INR', components: [{ code: 'BASIC', kind: 'earning', formula: 'BASIC' }] });
            setShowAddPt(true);
          }}>
            <Icon name="plus" size={13} color="currentColor" /> New template
          </Button>
        </div>
        <div>
          {payrollTemplates.length === 0 ? (
            <div style={{ padding: '16px 20px', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>No payroll templates yet.</div>
          ) : payrollTemplates.map((pt, i) => {
            const config = pt.components as unknown as { currency?: string; components?: PayrollTemplateComponent[] };
            const comps: PayrollTemplateComponent[] = Array.isArray(config)
              ? config as unknown as PayrollTemplateComponent[]
              : config?.components ?? [];
            const earningCount = comps.filter(c => c.kind === 'earning').length;
            const deductionCount = comps.filter(c => c.kind === 'deduction').length;
            const expanded = expandedPtId === pt.id;
            return (
              <div key={pt.id} style={{ borderBottom: i < payrollTemplates.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div
                  onClick={() => setExpandedPtId(expanded ? null : pt.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--neutral-soft)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{ fontSize: 11, color: 'var(--ink-3)', transition: 'transform 0.15s', display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'none' }}>▶</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink)' }}>{pt.name}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>
                      {earningCount} earning{earningCount !== 1 ? 's' : ''} · {deductionCount} deduction{deductionCount !== 1 ? 's' : ''}
                      {(config as { currency?: string })?.currency ? ` · ${(config as { currency?: string }).currency}` : ''}
                    </div>
                  </div>
                </div>
                {expanded && (
                  <div style={{ padding: '0 20px 14px 48px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
                      <thead>
                        <tr style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                          <th style={{ textAlign: 'left', padding: '4px 8px 8px', fontWeight: 600 }}>CODE</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px 8px', fontWeight: 600 }}>KIND</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px 8px', fontWeight: 600 }}>FORMULA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comps.map((c, ci) => (
                          <tr key={ci} style={{ borderTop: '1px solid var(--border)' }}>
                            <td style={{ padding: '7px 8px', fontFamily: 'monospace', fontWeight: 600, color: 'var(--ink)' }}>{c.code}</td>
                            <td style={{ padding: '7px 8px' }}>
                              <Chip variant={c.kind === 'earning' ? 'success' : c.kind === 'deduction' ? 'danger' : 'neutral'}>{c.kind}</Chip>
                            </td>
                            <td style={{ padding: '7px 8px', fontFamily: 'monospace', color: 'var(--ink-2)', fontSize: 12 }}>{c.formula}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Roles & access ── */}
      <div className="t-card" style={{ marginBottom: 20 }}>
        <div className="card-head" style={{ paddingBottom: 12 }}>
          <h3>Roles &amp; access</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 16px', background: 'var(--neutral-soft)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <Icon name="lock" size={16} color="var(--ink-3)" style={{ marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', lineHeight: 1.6 }}>
              Access follows the <strong>People role + HR org chart</strong>, not the Platform workspace-admin role — a manager and a workspace admin can be different people. Sensitive data (compensation, payslips, documents) is visible only to the employee, their approving manager where relevant, and HR Admin.
            </span>
          </div>
        </div>
      </div>

      {/* ── Add automation event modal ── */}
      {showAddEvent && (
        <Modal title="Add automation event" onClose={() => setShowAddEvent(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowAddEvent(false)}>Cancel</Button>
              <Button variant="accent" disabled={!newEventName.trim()} onClick={() => {
                const code = newEventName.trim().toLowerCase().replace(/\s+/g, '_');
                setCustomEvents(prev => [...prev, { code, label: newEventName.trim() }]);
                setShowAddEvent(false);
              }}>Add event</Button>
            </>
          }
        >
          <div className="form-row">
            <label>Event name</label>
            <input className="t-input" placeholder="e.g. Role change, Return from leave" value={newEventName} onChange={e => setNewEventName(e.target.value)} autoFocus />
          </div>
          <div className="form-row">
            <label>Description <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(optional)</span></label>
            <textarea className="t-input" rows={3} style={{ height: 'auto', padding: '8px 10px', resize: 'vertical' }} placeholder="When this event happens..." value={newEventDesc} onChange={e => setNewEventDesc(e.target.value)} />
          </div>
          <Alert variant="info">
            Custom events automate beyond the built-in lifecycle. After adding one, create a workflow for it in Workflows, then add an automation rule that maps the event to that workflow.
          </Alert>
        </Modal>
      )}

      {/* ── Add automation rule modal ── */}
      {showAddRule && (
        <Modal title="Add automation rule" onClose={() => setShowAddRule(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowAddRule(false)}>Cancel</Button>
              <Button variant="accent" disabled={!newRule.eventCode || !newRule.templateId} onClick={() => {
                setRules(prev => [...(prev ?? initialRules), { id: `rule_${Date.now()}`, ...newRule }]);
                setShowAddRule(false);
              }}>Add rule</Button>
            </>
          }
        >
          <div className="form-row">
            <label>When this event…</label>
            <select className="t-input" value={newRule.eventCode} onChange={e => setNewRule(r => ({ ...r, eventCode: e.target.value }))}>
              {allEvents.map(ev => <option key={ev.code} value={ev.code}>{ev.label}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label>…run this workflow</label>
            <select className="t-input" value={newRule.templateId} onChange={e => setNewRule(r => ({ ...r, templateId: e.target.value }))}>
              <option value="">Select a workflow template</option>
              {workflowTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label>Mode</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Switch checked={newRule.isAuto} onChange={checked => setNewRule(r => ({ ...r, isAuto: checked }))} />
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>{newRule.isAuto ? 'Auto-run' : 'Manual'}</span>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Add leave type modal ── */}
      {showAddLt && (
        <Modal title="Add leave type" onClose={() => setShowAddLt(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowAddLt(false)}>Cancel</Button>
              <Button variant="accent" disabled={!ltForm.name.trim() || !ltForm.code.trim() || addLtMutation.isPending}
                onClick={() => addLtMutation.mutate({ name: ltForm.name.trim(), code: ltForm.code.trim().toUpperCase() })}>
                {addLtMutation.isPending ? 'Saving…' : 'Add leave type'}
              </Button>
            </>
          }
        >
          <div className="form-row">
            <label>Name</label>
            <input className="t-input" placeholder="e.g. Earned Leave" value={ltForm.name} autoFocus onChange={e => {
              const name = e.target.value;
              setLtForm(f => ({
                ...f, name,
                code: f.code === '' || f.code === f.name.slice(0, 2).toUpperCase()
                  ? name.split(' ').map(w => w[0] ?? '').join('').toUpperCase().slice(0, 4)
                  : f.code,
              }));
            }} />
          </div>
          <div className="form-row">
            <label>Code</label>
            <input className="t-input" placeholder="e.g. EL" maxLength={8} value={ltForm.code}
              onChange={e => setLtForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
          </div>
          <Alert variant="info">Accrual policy (days per year, carry-over limit) is configured separately via leave policies after adding the type.</Alert>
        </Modal>
      )}

      {/* ── New payroll template modal ── */}
      {showAddPt && (
        <Modal title="New payroll template" onClose={() => setShowAddPt(false)} size="wide"
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowAddPt(false)}>Cancel</Button>
              <Button variant="accent"
                disabled={!ptForm.name.trim() || ptForm.components.length === 0 || ptForm.components.some(c => !c.code.trim() || !c.formula.trim()) || addPtMutation.isPending}
                onClick={() => addPtMutation.mutate()}>
                {addPtMutation.isPending ? 'Saving…' : 'Create template'}
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Template name</label>
              <input className="t-input" placeholder="e.g. Simple Payroll (No PF)" autoFocus value={ptForm.name}
                onChange={e => setPtForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style={{ width: 120 }}>
              <label style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Currency</label>
              <select className="t-input" value={ptForm.currency} onChange={e => setPtForm(f => ({ ...f, currency: e.target.value }))}>
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink-2)' }}>Components</label>
              <Button variant="ghost" size="sm" onClick={addPtComponent}>
                <Icon name="plus" size={12} color="currentColor" /> Add row
              </Button>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 1fr 32px', gap: 0, padding: '8px 12px', background: 'var(--neutral-soft)', borderBottom: '1px solid var(--border)' }}>
                {['CODE', 'KIND', 'FORMULA', ''].map((h, i) => (
                  <span key={i} style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--ink-3)', letterSpacing: '0.05em' }}>{h}</span>
                ))}
              </div>
              {ptForm.components.map((c, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 1fr 32px', gap: 8, padding: '8px 12px', alignItems: 'center', borderBottom: i < ptForm.components.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <input className="t-input" placeholder="e.g. BASIC" value={c.code}
                    onChange={e => updatePtComponent(i, { code: e.target.value.toUpperCase() })}
                    style={{ fontFamily: 'monospace', fontSize: 13 }} />
                  <select className="t-input" value={c.kind} onChange={e => updatePtComponent(i, { kind: e.target.value as PayrollTemplateComponent['kind'] })}>
                    <option value="earning">earning</option>
                    <option value="deduction">deduction</option>
                    <option value="employer">employer</option>
                  </select>
                  <input className="t-input" placeholder="e.g. BASIC or min(BASIC*0.12,1800)" value={c.formula}
                    onChange={e => updatePtComponent(i, { formula: e.target.value })}
                    style={{ fontFamily: 'monospace', fontSize: 13 }} />
                  <button type="button" onClick={() => removePtComponent(i)}
                    style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', color: 'var(--ink-3)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                </div>
              ))}
              {ptForm.components.length === 0 && (
                <div style={{ padding: '16px 12px', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>No components — click "Add row"</div>
              )}
            </div>
            <p style={{ marginTop: 8, fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', lineHeight: 1.6 }}>
              <strong>Formula</strong>: use component codes as variables (e.g. <code>BASIC</code>, <code>HRA</code>). Supports <code>min()</code>, <code>max()</code>, arithmetic. Later rows can reference earlier ones. Example deduction: <code>min(BASIC * 0.12, 1800)</code>
            </p>
          </div>
        </Modal>
      )}

      {/* ── Add holiday modal ── */}
      {showAddHol && (
        <Modal title="Add holiday" onClose={() => setShowAddHol(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowAddHol(false)}>Cancel</Button>
              <Button variant="accent" disabled={!holForm.name.trim() || !holForm.holidayDate || addHolMutation.isPending}
                onClick={() => {
                  if (!activeCalendar) return;
                  addHolMutation.mutate({ calendarId: activeCalendar.id, name: holForm.name.trim(), holidayDate: holForm.holidayDate, type: holForm.type });
                }}>
                {addHolMutation.isPending ? 'Saving…' : 'Add holiday'}
              </Button>
            </>
          }
        >
          <div className="form-row">
            <label>Holiday name</label>
            <input className="t-input" placeholder="e.g. Dussehra" value={holForm.name} autoFocus onChange={e => setHolForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="form-row">
            <label>Date</label>
            <DatePicker value={holForm.holidayDate} onChange={v => setHolForm(f => ({ ...f, holidayDate: v }))} placeholder="Select date" />
          </div>
          <div className="form-row">
            <label>Type</label>
            <select className="t-input" value={holForm.type} onChange={e => setHolForm(f => ({ ...f, type: e.target.value as 'national' | 'regional' | 'optional' }))}>
              <option value="national">National</option>
              <option value="regional">Regional</option>
              <option value="optional">Optional</option>
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
}
